import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CgGitFork } from "react-icons/cg";
import { AiFillStar } from "react-icons/ai";
import { BsSun, BsMoon } from "react-icons/bs";
import { FaLinkedinIn } from "react-icons/fa";
import Logo from "./Logo";
import { useTheme } from "../context/ThemeContext";

const NAV_ITEMS = [
  { label: "Home",       section: "hero"       },
  { label: "About",      section: "about"      },
  { label: "Experience", section: "experience" },
  { label: "Projects",   section: "projects"   },
  { label: "Blog",       section: "blog"       },
  { label: "Resume",     section: "resume"     },
  { label: "Contact",    section: "contact"    },
];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [active, setActive]     = useState("hero");
  const location                = useLocation();
  const navigate                = useNavigate();
  const isBlogDetail            = location.pathname.startsWith("/blog/");
  const { theme, toggleTheme }  = useTheme();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      for (const { section } of [...NAV_ITEMS].reverse()) {
        const el = document.getElementById(section);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActive(section);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function handleNavClick(section) {
    setOpen(false);
    if (isBlogDetail) {
      navigate("/");
      setTimeout(() => scrollToSection(section), 300);
    } else {
      scrollToSection(section);
    }
  }

  return (
    <nav className={`nav-wrapper${scrolled ? " scrolled" : ""}`}>
      <div className="nav-inner">

        {/* ── Logo ── */}
        <button
          className="nav-logo-btn"
          onClick={() => handleNavClick("hero")}
          aria-label="Go to home"
        >
          <Logo size={38} />
          <span className="nav-logo-text">
            <span className="nav-logo-m">M</span>
            <span className="nav-logo-separator">.</span>
            <span className="nav-logo-m">Muaaz</span>
          </span>
        </button>

        {/* ── Hamburger ── */}
        <button
          className={`nav-hamburger${open ? " open" : ""}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          <span /><span /><span />
        </button>

        {/* ── Links ── */}
        <ul className={`nav-links${open ? " open" : ""}`}>
          {NAV_ITEMS.map(({ label, section }) => (
            <li className="nav-link-item" key={section}>
              <button
                className={active === section && !isBlogDetail ? "active" : ""}
                onClick={() => handleNavClick(section)}
              >
                {label}
              </button>
            </li>
          ))}

          {/* ── GitHub button ── */}
          <li className="nav-link-item">
            <a
              href="https://github.com/MuhammadMuaaz007/"
              target="_blank"
              rel="noreferrer"
              className="nav-github-btn"
              aria-label="GitHub"
            >
              <CgGitFork /> <AiFillStar />
            </a>
          </li>

          {/* ── LinkedIn button ── */}
          <li className="nav-link-item">
            <a
              href="https://www.linkedin.com/in/muhammad-muaaz-120237297/"
              target="_blank"
              rel="noreferrer"
              className="nav-linkedin-btn"
              aria-label="LinkedIn"
            >
              <FaLinkedinIn />
            </a>
          </li>

          {/* ── Theme toggle ── */}
          <li className="nav-link-item">
            <button
              className="nav-theme-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? <BsSun className="theme-icon" /> : <BsMoon className="theme-icon" />}
              <span className="theme-btn-label">{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
